import PrismicRichText from 'prismic-richtext';
import { compose, is, prop, replace, trim } from 'ramda';

import { isNotVoid, whenNotNil } from '../../../helpers/compact';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
export type RichTextFormat = { type: string; text: string; spans: any[] };

/**
 * Transform the given input to TEXT using prismic-dom
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const asText = (val: any): string | void =>
  val instanceof Array ? PrismicRichText.asText(val, ' ') : val;

/**
 * Sanitize the given input string (remove breakline characters)
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const sanitize: (val: any) => string = compose(trim, replace(/\r?\n|\r/g, ''));

/**
 * Sanitize the result of the toText function for the given input
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const toText: (val: any) => string | undefined = compose(
  whenNotNil(sanitize),
  asText
);

/**
 * Sanitize the result of the toText function for the given input
 *
 * @param {string} p The property to transform
 * @param  {Object[]} val
 * @return {string | void}
 */
export const toTextFromProp = (
  key: string,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  object: Record<string, any>
): string | void => toText(prop(key, object));

const textIsNotEmpty = compose(isNotVoid, prop('text'));

/**
 * Transform string to valid primic richtext
 *
 * @param  {Object[]|string} val
 * @return {string}
 */
export const toRichText = (
  val: RichTextFormat[] | string | void
): RichTextFormat[] | void => {
  if (!val) {
    return undefined;
  }

  let richtext: RichTextFormat[];

  // Transfrom simple string to rich text prismic format
  if (is(String, val)) {
    richtext = [
      {
        type: 'paragraph',
        text: sanitize(val),
        spans: [],
      },
    ];
  } else {
    richtext = val as RichTextFormat[];
  }

  return richtext.filter(textIsNotEmpty);
};

/**
 * Extract and transform prop to valid primic richtext
 *
 * @param {string} p The property to transform
 * @param  {Object[]} val
 * @return {string}
 */
export const toRichTextFromProp = (
  key: string,
  object: Record<string, RichTextFormat[] | string | void>
): RichTextFormat[] | void => toRichText(prop(key, object));
